import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ChevronLeft, Download, FileText, Clock, Users, AlertTriangle, Crown, Shield, Trash2 } from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
const api = axios.create({ baseURL: API, withCredentials: true });

function fmtDuration(s) {
  if (s == null) return "—";
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), ss = s % 60;
  return h > 0 ? `${h}h ${m}m ${ss}s` : m > 0 ? `${m}m ${ss}s` : `${ss}s`;
}
function shortId(s) { return s ? String(s).slice(0, 10) + "…" : "—"; }

function RoleChip({ role }) {
  const map = {
    host: "bg-amber-400/15 text-amber-300 border-amber-400/40",
    dj: "bg-[#FF007F]/15 text-[#FF007F] border-[#FF007F]/40",
    cohost: "bg-[#00F0FF]/15 text-[#00F0FF] border-[#00F0FF]/40",
    participant: "bg-white/5 text-white/60 border-white/10",
  };
  return <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide font-bold border ${map[role] || map.participant}`}>{role}</span>;
}

export default function RoomReport() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const [rep, setRep] = useState(null);
  const [err, setErr] = useState(null);
  const [auditOpen, setAuditOpen] = useState(false);

  useEffect(() => {
    api.get(`/rooms/${roomId}/report`).then((r) => setRep(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Failed to load report"));
  }, [roomId]);

  if (err) return (
    <div className="min-h-screen bg-[#050508] text-white grid place-items-center p-8">
      <div className="sr-glass rounded-xl p-6 max-w-md text-center" data-testid="report-error">
        <AlertTriangle className="w-8 h-8 text-amber-400 mx-auto mb-2" />
        <p className="text-white/70">{err}</p>
        <button onClick={() => navigate("/")} className="mt-4 px-4 py-2 bg-white/10 rounded">Back</button>
      </div>
    </div>
  );
  if (!rep) return <div className="min-h-screen bg-[#050508] text-white grid place-items-center" data-testid="report-loading">Loading…</div>;

  const downloadJson = () => {
    const blob = new Blob([JSON.stringify(rep, null, 2)], { type: "application/json" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `stackroom-${roomId}-report.json`; a.click();
  };

  return (
    <div className="min-h-screen bg-[#050508] text-white" data-testid="room-report-page">
      <div className="max-w-5xl mx-auto px-6 py-8">
        <Link to="/" className="inline-flex items-center gap-1 text-xs text-white/40 hover:text-white/80 mb-4">
          <ChevronLeft className="w-3.5 h-3.5" /> Back to dashboard
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
          <div>
            <h1 className="font-display text-3xl font-bold" data-testid="report-room-name">{rep.room?.name}</h1>
            <p className="text-white/40 text-xs font-mono mt-1">{roomId} · {rep.room?.status}</p>
          </div>
          <div className="flex gap-2">
            <button onClick={downloadJson} className="flex items-center gap-1.5 px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/15 rounded text-xs font-bold uppercase tracking-wider" data-testid="report-download-json">
              <Download className="w-3.5 h-3.5" /> JSON
            </button>
            <a href={`${API}/rooms/${roomId}/report.csv`} className="flex items-center gap-1.5 px-3 py-2 bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-[#0A0A0F] rounded text-xs font-bold uppercase tracking-wider" data-testid="report-download-csv">
              <Download className="w-3.5 h-3.5" /> CSV
            </a>
          </div>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="report-summary-cards">
          <SummaryCard icon={<Clock className="w-4 h-4" />} label="Duration" value={fmtDuration(rep.duration_seconds)} />
          <SummaryCard icon={<Users className="w-4 h-4" />} label="Participants" value={rep.participants.length} />
          <SummaryCard icon={<Shield className="w-4 h-4" />} label="Cohost promotions" value={rep.moderation_summary?.cohost_promotions || 0} />
          <SummaryCard icon={<Crown className="w-4 h-4" />} label="Reopens" value={rep.reopen_count || 0} />
        </div>

        {/* Participants */}
        <section className="mb-8">
          <h2 className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-bold mb-2">Participants</h2>
          <div className="sr-glass rounded-lg overflow-hidden">
            <table className="w-full text-sm" data-testid="report-participants-table">
              <thead className="bg-white/5 text-[10px] uppercase tracking-wider text-white/40">
                <tr><th className="text-left px-3 py-2">Name</th><th className="text-left px-3 py-2">Roles</th><th className="text-left px-3 py-2">Total time</th><th className="text-left px-3 py-2">First seen</th><th className="text-left px-3 py-2">Status</th></tr>
              </thead>
              <tbody>
                {rep.participants.length === 0 && <tr><td colSpan={5} className="text-center text-white/40 py-6">No participants recorded.</td></tr>}
                {rep.participants.map((p) => (
                  <tr key={p.identity} className="border-t border-white/5" data-testid={`participant-row-${p.identity}`}>
                    <td className="px-3 py-2">
                      <div className="font-medium">{p.name_override || p.display_name || "Participant"}</div>
                      <div className="text-[10px] text-white/30">Participant record</div>
                    </td>
                    <td className="px-3 py-2"><div className="flex flex-wrap gap-1">{(p.roles_held || []).map((r) => <RoleChip key={r} role={r} />)}</div></td>
                    <td className="px-3 py-2 text-white/70">{fmtDuration(p.total_seconds_in_room)}</td>
                    <td className="px-3 py-2 text-white/50 text-[11px]">{p.first_seen ? new Date(p.first_seen).toLocaleString() : "—"}</td>
                    <td className="px-3 py-2">{p.was_banned ? <span className="text-rose-400 text-xs">banned</span> : p.was_kicked ? <span className="text-amber-300 text-xs">waiting</span> : <span className="text-white/40 text-xs">active</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* DJ history */}
        <section className="mb-8">
          <h2 className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-bold mb-2">DJ history</h2>
          {(rep.dj_history || []).length === 0 ? (
            <div className="text-white/40 text-sm italic">No DJ handoffs recorded.</div>
          ) : (
            <ol className="space-y-1.5 text-sm" data-testid="report-dj-history">
              {rep.dj_history.map((h, i) => (
                <li key={i} className="sr-glass rounded px-3 py-2 flex justify-between text-white/70">
                  <span className="text-xs font-medium">{h.username || "DJ"}</span>
                  <span className="text-white/40 text-xs">{h.from ? new Date(h.from).toLocaleString() : ""} → {h.until ? new Date(h.until).toLocaleString() : "now"}</span>
                </li>
              ))}
            </ol>
          )}
        </section>

        {/* Audit log */}
        <section>
          <button onClick={() => setAuditOpen((v) => !v)} className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-white/40 font-bold mb-2 hover:text-white/70" data-testid="report-audit-toggle">
            <FileText className="w-3.5 h-3.5" /> Audit log ({rep.audit_log.length}) {auditOpen ? "▾" : "▸"}
          </button>
          {auditOpen && (
            <ol className="space-y-1 text-xs font-mono" data-testid="report-audit-list">
              {rep.audit_log.map((row, i) => (
                <li key={i} className="sr-glass rounded px-3 py-1.5 text-white/70">
                  <span className="text-white/40">{row.at?.slice(0,19).replace("T"," ")}</span>{" · "}
                  <span className="text-[#00F0FF]">{row.event}</span>{" · "}
                  <span className="text-white/50">{row.actor_username || "System"}</span>
                  {row.target_username && <> → <span className="text-white/50">{row.target_username}</span></>}
                </li>
              ))}
            </ol>
          )}
        </section>
      </div>
    </div>
  );
}

function SummaryCard({ icon, label, value }) {
  return (
    <div className="sr-glass rounded-lg p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-white/40 font-bold mb-1">{icon}{label}</div>
      <div className="text-2xl font-bold text-white">{value}</div>
    </div>
  );
}

export function AccountPrivacy() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [dataReport, setDataReport] = useState(null);
  const [dataReportErr, setDataReportErr] = useState("");

  useEffect(() => {
    api.get("/auth/me").then((r) => setMe(r.data?.user)).catch(() => navigate("/login"));
    api.get("/me/data/report").then((r) => setDataReport(r.data)).catch((e) => setDataReportErr(e?.response?.data?.detail || "Your report is temporarily unavailable."));
  }, [navigate]);

  const requestDelete = async () => {
    try { await api.post("/me/data/delete-request", {}); toast.success("Deletion requested — your account will be removed within 24 hours."); setOpen(false); setTyped(""); const r = await api.get("/auth/me"); setMe(r.data?.user); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const cancelDelete = async () => {
    try { await api.post("/me/data/delete-cancel", {}); toast.success("Deletion cancelled."); const r = await api.get("/auth/me"); setMe(r.data?.user); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!me) return null;
  const pending = me.deletion_requested_at;

  return (
    <div className="min-h-screen bg-[#050508] text-white" data-testid="account-privacy-page">
      <div className="max-w-2xl mx-auto px-6 py-10">
        <Link to="/" className="inline-flex items-center gap-1 text-xs text-white/40 hover:text-white/80 mb-6"><ChevronLeft className="w-3.5 h-3.5" /> Back to dashboard</Link>
        <h1 className="font-display text-3xl font-bold mb-2">Account & privacy</h1>
        <p className="text-white/50 text-sm mb-8">Manage the data Full Spectrum Center holds about you.</p>

        <section className="sr-glass rounded-xl p-5 mb-6" data-testid="data-transparency-report">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <h2 className="font-bold text-base">Your data transparency report</h2>
              <p className="text-white/55 text-xs mt-1">A live inventory of the information associated with your username, why it is used, and how long it is kept.</p>
            </div>
            <Shield className="w-5 h-5 text-[#00F0FF] shrink-0" />
          </div>
          {dataReportErr && <p className="text-amber-200 text-xs border border-amber-400/25 bg-amber-400/10 rounded p-3">{dataReportErr}</p>}
          {!dataReport && !dataReportErr && <p className="text-white/45 text-xs">Preparing your report…</p>}
          {dataReport && <>
            <div className="grid sm:grid-cols-2 gap-3">
              {(dataReport.categories || []).map((category) => (
                <article key={category.id} className="rounded-lg border border-white/10 bg-black/15 p-3">
                  <div className="flex justify-between gap-2 items-start"><h3 className="text-sm font-bold">{category.label}</h3><span className={`text-[10px] uppercase tracking-wider font-bold ${category.stored ? "text-[#00F0FF]" : "text-white/45"}`}>{category.stored ? `${category.count} record${category.count === 1 ? "" : "s"}` : "Not retained"}</span></div>
                  <p className="text-white/60 text-xs mt-2">{category.purpose}</p>
                  <p className="text-white/40 text-[11px] mt-2"><span className="text-white/55 font-bold">Kept:</span> {category.retention}</p>
                </article>
              ))}
            </div>
            <p className="text-white/35 text-[11px] mt-4">Generated {dataReport.generated_at ? new Date(dataReport.generated_at).toLocaleString() : "now"}. Live audio/video is not recorded by default; any recording appears here only when it was explicitly uploaded.</p>
          </>}
        </section>

        <section className="sr-glass rounded-xl p-5 mb-6">
          <h2 className="font-bold text-base mb-1">Your data</h2>
            <p className="text-white/60 text-xs mb-4">Your display username, consent record, hashed security signals, and account-linked moderation references are managed under the <Link to="/legal/privacy" className="text-[#00F0FF] underline">Privacy Policy</Link>.</p>

          {pending ? (
            <div className="bg-rose-500/10 border border-rose-500/30 rounded p-3" data-testid="deletion-pending-card">
              <p className="text-rose-200 text-sm font-bold mb-1">Deletion requested</p>
              <p className="text-white/60 text-xs mb-3">Your account will be removed within 24 hours of <span className="font-mono">{new Date(pending).toLocaleString()}</span>. You can still cancel.</p>
              <button onClick={cancelDelete} className="px-4 py-2 bg-white/10 hover:bg-white/15 border border-white/15 rounded text-xs font-bold uppercase tracking-wider" data-testid="cancel-delete-btn">Cancel deletion</button>
            </div>
          ) : (
            <button onClick={() => setOpen(true)} className="flex items-center gap-1.5 px-4 py-2 bg-rose-500/15 hover:bg-rose-500/25 border border-rose-500/40 text-rose-200 rounded text-xs font-bold uppercase tracking-wider" data-testid="request-delete-btn">
              <Trash2 className="w-3.5 h-3.5" /> Request data deletion
            </button>
          )}
        </section>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur grid place-items-center p-4" data-testid="delete-confirm-modal">
          <div className="sr-glass rounded-2xl p-5 max-w-sm w-full">
            <h3 className="font-bold text-base mb-1">Confirm data deletion</h3>
            <p className="text-white/60 text-xs mb-3">This queues your account for permanent removal within 24 hours. Audit-log entries referencing you will be anonymized. To confirm, type <span className="font-mono font-bold text-rose-300">DELETE</span> below.</p>
            <input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="DELETE" className="w-full bg-white/5 border border-white/15 rounded px-3 py-2 text-sm font-mono mb-3" data-testid="delete-confirm-input" />
            <div className="flex justify-end gap-2">
              <button onClick={() => { setOpen(false); setTyped(""); }} className="px-3 py-2 text-xs font-bold bg-white/5 border border-white/15 rounded uppercase tracking-wider">Cancel</button>
              <button onClick={requestDelete} disabled={typed !== "DELETE"} className="px-4 py-2 text-xs font-bold bg-rose-500 text-white rounded uppercase tracking-wider disabled:opacity-40" data-testid="delete-confirm-btn">Confirm</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
